import {I18nKFunction} from 'i18n';
import React from 'react';
import {FormFieldRadioButton} from '@yandex-data-ui/cloud-components';
import block from 'bem-cn-lite';
import _ from 'lodash';
import {useFieldArray} from 'react-final-form-arrays';
import {compose} from 'recompose';

import ComputeFormFields, {
    ComputeFormFieldsValues,
} from 'components/calculator/ComputeFormFields/ComputeFormFields';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {getInitialComputeValues} from 'components/calculator/ComputeFormFields/utils';
import ClosableSection from 'components/calculator/ClosableSection/ClosableSection';
import CalculatorForm from 'components/calculator/CalculatorForm/CalculatorForm';
import {FormFieldRangeInputPicker} from 'components/formFields';
import AddButton from 'components/Buttons/AddButton/AddButton';
import {ComputeConfig} from 'components/calculator/models';
import withData from 'components/calculator/withData';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {LocaleData} from 'contexts/LocaleContext';
import {Field, useForm, useFormState} from 'react-final-form';
import {getConfig} from 'units/calculator/api';

import './KubernetesForm.scss';

const b = block('KubernetesForm');

const getMasterSpecOptions = _.memoize((i18nK: I18nKFunction) => [
    {
        content: i18nK('value_master-spec-zonal'),
        value: 'zonal',
    },
    {
        content: i18nK('value_master-spec-regional'),
        value: 'regional',
    },
]);

interface NodeGroupsFormValues extends ComputeFormFieldsValues {
    fixedScale: number;
}

interface KubernetesFormValues {
    masterSpec: 'zonal' | 'regional';
    nodeGroups: NodeGroupsFormValues[];
}

interface KubernetesFormOwnProps {
    title: string;
    locale: LocaleData;
}

interface KubernetesFormDataProps {
    data: ComputeConfig;
}

type KubernetesFormProps = KubernetesFormDataProps & KubernetesFormOwnProps & WithTranslationProps;

const KubernetesForm: React.FC<KubernetesFormProps> = (props) => {
    const {
        title,
        data: {osProducts, platforms},
        i18nK,
        i18n,
    } = props;

    const {fields} = useFieldArray<NodeGroupsFormValues>('nodeGroups');
    const {initialize} = useForm<KubernetesFormValues>();
    const {values, initialValues} = useFormState<KubernetesFormValues>();

    const createComputeData = React.useCallback(
        (): NodeGroupsFormValues => ({
            ...getInitialComputeValues({osProducts, platforms}, i18n),
            fixedScale: 1,
        }),
        [osProducts, platforms, i18n],
    );

    const masterSpecOptions = getMasterSpecOptions(i18nK);

    React.useEffect(() => {
        initialize({
            masterSpec: 'zonal',
            nodeGroups: [createComputeData()],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!initialValues) {
        return null;
    }

    return (
        <CalculatorForm title={title} className={b()}>
            <CalculatorFormLayout>
                <CalculatorFormLayout.Section>
                    <CalculatorFormLayout.Row
                        title={i18nK('field_master-spec')}
                        note={i18nK('context_master-spec')}
                    >
                        <Field name="masterSpec">
                            {(fieldProps) => (
                                <FormFieldRadioButton
                                    {...fieldProps}
                                    size="l"
                                    controlClassName={b('master-spec')}
                                    options={masterSpecOptions}
                                    width="max"
                                />
                            )}
                        </Field>
                    </CalculatorFormLayout.Row>
                </CalculatorFormLayout.Section>
                {fields.map((name, index) => (
                    <CalculatorFormLayout.Section key={index}>
                        <ClosableSection
                            showClose={index !== 0}
                            onClose={() => fields.remove(index)}
                        >
                            <ComputeFormFields
                                values={values.nodeGroups[index]}
                                platforms={platforms}
                                osProducts={osProducts}
                                fieldName={name}
                                disableOperationSystem={true}
                                disableRepeatableDisks={true}
                                disableTraffic={true}
                            />
                            <CalculatorFormLayout.Row
                                title={i18nK('field_fixed-scale')}
                                note={i18nK('context_fixed-scale')}
                            >
                                <Field name={`${name}.fixedScale`}>
                                    {(fieldProps) => (
                                        <FormFieldRangeInputPicker
                                            {...fieldProps}
                                            size="l"
                                            minValue={1}
                                            maxValue={32}
                                            step={1}
                                        />
                                    )}
                                </Field>
                            </CalculatorFormLayout.Row>
                        </ClosableSection>
                    </CalculatorFormLayout.Section>
                ))}
                <CalculatorFormLayout.Section>
                    <AddButton onClick={() => fields.push(createComputeData())}>
                        {i18nK('button_add-instance')}
                    </AddButton>
                </CalculatorFormLayout.Section>
            </CalculatorFormLayout>
        </CalculatorForm>
    );
};

const formDataLoader = async (props: KubernetesFormProps) => {
    const {locale} = props;
    return getConfig(locale, 'managed-kubernetes');
};

export default compose<KubernetesFormProps, KubernetesFormOwnProps>(
    withData({loader: formDataLoader}),
    withTranslation('calculator'),
)(KubernetesForm);
